/* import __COLOCATED_TEMPLATE__ from './chip-selector.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import clsx from 'clsx';

const CUSTOM_OPTION_DEFAULT_TEXT = '';
const CUSTOM_OPTION_RESERVED_MODEL = 'GLM-chip-selector-custom-input-option';

/**
 * @param {HTMLElement} element
 * @returns {CanvasRenderingContext2D}
 */
const createContext = (element) => {
  const { fontSize, fontFamily } = window.getComputedStyle(element);

  const context = document
    .createElement('canvas')
    .getContext('2d');

  context.font = `${fontSize} ${fontFamily}`;
  return context;
};

const CLASS_NAME = 'GLM-chip-selector';
const CLASS_NAME_NO_FILTERING = `${CLASS_NAME}--no-filtering`;
const EMPTY_OPTIONS = [];

/** @type {object} */
const THEMES = {
  BLACK: 'black',
  WHITE: 'white',
};

const THEME_TO_CLASS_MAP = Object.keys(THEMES).reduce((accum, themeKey) => {
  const theme = THEMES[themeKey];
  accum[theme] = `${CLASS_NAME}--theme-${theme}`;
  return accum;
}, {});

/** @module */
class GlmChipSelector extends Component {
  /** @var {CanvasRenderingContext2D|null} */
  @tracked context = null;

  @tracked enteringCustom = false;

  /**
   * Compute the width used by typed text
   */
  #textWidthCache = createCache(() => {
    return this.context?.measureText(this.args.value ?? '').width ?? -1;
  });

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
      this.args.disableFiltering && CLASS_NAME_NO_FILTERING,
      this.theme,
    );
  }

  get chipContainerClassNames() {
    const CLASS_NAME_CHIP_CONTAINER = `${CLASS_NAME}__chip-container`;
    return clsx(
      this.args.chipContainerClassName,
      CLASS_NAME_CHIP_CONTAINER,
    );
  }

  get chipClassNames() {
    const CLASS_NAME_CHIP = `${CLASS_NAME}__chip`;
    return clsx(
      this.args.chipClassName,
      CLASS_NAME_CHIP,
      this.args.disabled && `${CLASS_NAME_CHIP}--disabled`,
    );
  }

  get limitReached() {
    const limit = Number.parseInt(this.args.limit);
    if (!limit) {
      return false;
    }

    return this.selected.length >= limit;
  }

  get inputStyles() {
    const { context, textWidth } = this;
    if (!context) {
      return null;
    }

    return htmlSafe(`
      flex-basis: ${textWidth}px;
    `);
  }

  get options() {
    const suppliedOptions = this.args.options;
    const builtInOptions = [];

    // allow custom text input to become a chip
    if (this.args.allowCustom) {
      builtInOptions.push({
        key: this.args.allowCustomText || CUSTOM_OPTION_DEFAULT_TEXT,
        model: CUSTOM_OPTION_RESERVED_MODEL,
      });
    }

    return [
      ...suppliedOptions,
      ...builtInOptions,
    ]
  }

  get textWidth() {
    return getValue(this.#textWidthCache);
  }

  get theme() {
    const { theme } = this.args;
    return THEME_TO_CLASS_MAP[theme || THEMES.BLACK];
  }

  get selected() {
    return this.args.selected ?? EMPTY_OPTIONS;
  }

  get useOptions() {
    if (this.enteringCustom || this.limitReached) {
      return false;
    }

    return this.args.useOptions;
  }

  /**
   * @param {HTMLElement} element
   */
  @action
  refCallback(element) {
    if (element) {
      this.context = createContext(element);
    }
  }

  /**
   *
   */
  @action
  handleBlur() {
    const value = this.args.value.trim();
    if (value && this.enteringCustom) {
      this.args.oncustomadded?.({
        key: value,
      });
    }

    this.enteringCustom = false;

    this.args.onblur?.();
  }

  /**
   *
   */
  @action
  handleChipAdded(option) {
    // only make selection if it is a real option
    if (CUSTOM_OPTION_RESERVED_MODEL === option.model) {
      this.enteringCustom = true;
    } else {
      this.args.onadded?.(option);
    }
  }

  /**
   * @param {KeyboardEvent} e
   */
  @action
  handleKeyDown(e) {
    this.args.onkeydown?.(e);

    const {
      disabled,
      value,
    }  = this.args;

    const {
      disableFiltering,
      enteringCustom,
      limitReached,
      selected,
    } = this;

    if (e.defaultPrevented || disabled) {
      return;
    }

    // prevent option filtering when at limit
    if (
      limitReached
      && !disableFiltering
      && e.key !== 'Backspace'
      && e.key !== 'Enter'
      && e.key !== 'Tab'
    ) {
      e.preventDefault();
    }

    switch (e.key) {
      case 'Enter':
        const trimmedValue = value.trim();
        if (trimmedValue && enteringCustom) {
          // add chip upon hitting enter
          this.enteringCustom = false;
          this.args.oncustomadded?.({
            key: trimmedValue,
          });
        } else if (enteringCustom) {
          this.enteringCustom = false;
        }

        break;

      case 'Backspace':
        if (!value && enteringCustom) {
          // turn 'custom input' off by backspacing
          this.enteringCustom = false;
        } else if (!value && selected.length > 0) {
          // allow chip deletion using backspace key
          this.args.onremoved?.(selected[selected.length - 1]);
        }

        break;
    }
  }
}

export default GlmChipSelector;
